import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen relative">
      {/* Mystical Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/3315dd06c3e67642f54e90e5d56d0279.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.85
        }}
      />

      {/* Gradient Overlay for better text readability */}
      <div 
        className="fixed inset-0 z-0 bg-gradient-to-b from-purple-900/20 via-transparent to-pink-900/20"
      />

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white drop-shadow-lg mb-6">
                Privacy Policy
              </h1>
              <p className="text-white/90 drop-shadow text-lg">
                Last updated: January 1, 2025
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="prose prose-lg max-w-none"
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Introduction</h2>
                <p className="text-gray-700 leading-relaxed">
                  At OhMyGAWD (Guidance, Awareness, Wisdom, and Direction), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you use our AI-powered oracle service.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">OECD AI Principles Compliance</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  OhMyGAWD is built in accordance with the <strong>OECD Principles on Artificial Intelligence</strong>, which promote AI that is:
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-purple-600 text-xl mt-1"></i>
                    <div>
                      <strong>Inclusive growth, sustainable development and well-being:</strong> Our AI is designed to benefit users while respecting human rights and values.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-purple-600 text-xl mt-1"></i>
                    <div>
                      <strong>Human-centered values and fairness:</strong> We prioritize user wellbeing and ensure our AI operates fairly and without discrimination.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-purple-600 text-xl mt-1"></i>
                    <div>
                      <strong>Transparency and explainability:</strong> We are open about how our AI works and the limitations of its guidance.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-purple-600 text-xl mt-1"></i>
                    <div>
                      <strong>Robustness, security and safety:</strong> We implement strong security measures to protect your data and ensure reliable service.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-purple-600 text-xl mt-1"></i>
                    <div>
                      <strong>Accountability:</strong> We take responsibility for the proper functioning of our AI system and its impact on users.
                    </div>
                  </li>
                </ul>
                <p className="text-gray-600 text-sm mt-4">
                  Learn more about the OECD AI Principles at{' '}
                  <a 
                    href="https://www.oecd.org/en/topics/ai-principles.html" 
                    target="_blank" 
                    rel="nofollow noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 cursor-pointer"
                  >
                    oecd.org/en/topics/ai-principles.html
                  </a>
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Information We Collect</h2>
                <h3 className="font-semibold text-xl text-purple-800 mb-3">For All Users:</h3>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Questions you submit to the oracle
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Responses generated by our AI
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Basic usage data and analytics
                  </li>
                </ul>

                <h3 className="font-semibold text-xl text-purple-800 mb-3">For Subscribers:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Email address and account credentials
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Payment information (processed securely through our payment provider)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Query history and profile preferences
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Character customization settings
                  </li>
                </ul>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Provide and improve our AI oracle service
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Process your questions and generate personalized responses
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Maintain your account and subscription
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Store your query history (for subscribers)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Communicate with you about your account and our services
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Analyze usage patterns to enhance user experience
                  </li>
                </ul>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Data Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. All data transmissions are encrypted using SSL/TLS protocols. Payment information is processed through secure, PCI-compliant payment processors and is never stored on our servers.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Data Retention</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>One-time users:</strong> Questions and responses are stored temporarily for service delivery and may be anonymized for service improvement.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Subscribers:</strong> Your query history and profile data are retained for the duration of your subscription and for a reasonable period after cancellation to allow for account reactivation. You may request deletion of your data at any time.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Your Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Access your personal data
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Correct inaccurate information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Request deletion of your data
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Export your query history
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Opt-out of marketing communications
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    Cancel your subscription at any time
                  </li>
                </ul>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Third-Party Services</h2>
                <p className="text-gray-700 leading-relaxed">
                  We use trusted third-party services for payment processing, analytics, and AI infrastructure. These providers are contractually obligated to protect your data and use it only for the purposes we specify. We do not sell your personal information to third parties.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Cookies and Tracking</h2>
                <p className="text-gray-700 leading-relaxed">
                  We use cookies and similar technologies to enhance your experience, remember your preferences, and analyze site usage. You can control cookie settings through your browser preferences.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed">
                  OhMyGAWD is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
                </p>
              </div>

              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-sm">
                <h2 className="font-serif text-3xl font-bold text-purple-900 mb-4">Changes to This Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of OhMyGAWD after such changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <section>
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us:
                </p>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-center gap-3">
                    <i className="ri-mail-line text-gray-900"></i>
                    <a href="mailto:privacy@ohmygawd.ai" className="text-gray-900 hover:text-purple-600 cursor-pointer">
                      privacy@ohmygawd.ai
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-customer-service-line text-gray-900"></i>
                    <a href="/support" className="text-gray-900 hover:text-purple-600 cursor-pointer">
                      Visit our Support page
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-building-line text-gray-900"></i>
                    <span>Cybernetix Technologies</span>
                  </p>
                </div>
              </section>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPage;